/**
 * The wizard rail — a non-linear MUI Stepper across the top of the create
 * dialog. Every step is reachable at any time (owner ruling: ten FLAT steps);
 * the caption under each label is the live one-line summary the dialog computes.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Step, StepButton, StepLabel, Stepper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { STRATEGY_LIST } from '@test-kit/selectors/strategy-list';
import type { WizardRailItem, WizardStepKey } from './wizardSteps';
import { wizardStepIndex } from './wizardSteps';

export interface WizardRailProps {
  items: WizardRailItem[];
  activeStep: WizardStepKey;
  onStepChange: (key: WizardStepKey) => void;
}

/**
 * Width the discs and their captions need before the connectors start colliding
 * with the numerals (Task B9d). Below it the rail scrolls INSIDE its own strip
 * instead of widening the dialog, so the 800px body never inherits a min-width
 * from the chrome (design-principles §1: 900px is a supported admin width).
 *
 * REVIEW tier 2, MF-4: at ten steps 1080px was ~108px per step; the eleventh
 * (W13 IS2) squeezed every slot below the label width and «Building blocks» was
 * cut mid-word at 900px. The track now asks for the same ~108px PER STEP, so it
 * scrolls rather than compresses.
 */
export const WIZARD_RAIL_STEP_MIN_WIDTH = 108;
export const WIZARD_RAIL_MIN_WIDTH = 1080;

/**
 * The `scrollLeft` that centres `active` inside `strip` (MF-4, fix round 4):
 * the step's position in the TRACK is its rect relative to the strip's rect
 * plus what the strip has already scrolled — never `offsetLeft`, which MUI's
 * positioned `MuiStep-root` makes relative to the step itself. Clamped to the
 * strip's real range, so the last steps land flush at the end rather than
 * asking for a position the strip cannot reach. `null` when the boxes cannot
 * be measured (jsdom): the caller falls back to `scrollIntoView`.
 */
export function centredScrollLeft(strip: HTMLElement, active: HTMLElement): number | null {
  const stripRect = strip.getBoundingClientRect();
  const rect = active.getBoundingClientRect();
  if (!(rect.width > 0) || !(stripRect.width > 0) || !(strip.clientWidth > 0)) {
    if (typeof active.scrollIntoView === 'function') {
      active.scrollIntoView({ block: 'nearest', inline: 'center' });
    }
    return null;
  }
  const inTrack = rect.left - stripRect.left + strip.scrollLeft;
  const target = inTrack - (strip.clientWidth - rect.width) / 2;
  const max = Math.max(0, strip.scrollWidth - strip.clientWidth);
  return Math.round(Math.min(max, Math.max(0, target)));
}

export const WizardRail: React.FC<WizardRailProps> = ({ items, activeStep, onStepChange }) => {
  const stripRef = useRef<HTMLElement | null>(null);
  /** MF-4: the strip overflows, so the edges say so. */
  const [overflow, setOverflow] = useState({ start: false, end: false });

  const measureOverflow = useCallback(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const max = strip.scrollWidth - strip.clientWidth;
    setOverflow((prev) => {
      const next = { start: strip.scrollLeft > 1, end: max > 1 && strip.scrollLeft < max - 1 };
      return prev.start === next.start && prev.end === next.end ? prev : next;
    });
  }, []);

  /**
   * The active step must be reachable at every width: when the strip scrolls,
   * CENTRE it instead of leaving steps 10–11 off-canvas (MF-4).
   *
   * The scroll position is computed and assigned directly rather than left to
   * `scrollIntoView`'s ancestor walk (which the review found not to land on the
   * active step); `scrollIntoView` stays the fallback where the element cannot
   * be measured — jsdom reports every box as 0, so one test asserts THAT call.
   *
   * Fix round 4: the position is measured with `getBoundingClientRect` AGAINST
   * THE STRIP. Round 2 measured why the first version never moved: `offsetLeft`
   * is relative to the step's own `MuiStep-root` (MUI positions it), so it read
   * −8 for a step that sat 1103px into the track, and `max(0, −8 − 366)` was 0
   * on every change.
   */
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return undefined;
    const active = strip.querySelector<HTMLElement>(
      `[data-testid="${STRATEGY_LIST.wizardRailStep(activeStep)}"]`,
    );
    if (!active) return undefined;
    // One frame later: MUI's Stepper lays its connectors out on mount, and a
    // measurement taken in the same commit reads the pre-layout width.
    const frame = requestAnimationFrame(() => {
      strip.scrollLeft = centredScrollLeft(strip, active) ?? strip.scrollLeft;
      measureOverflow();
    });
    return () => cancelAnimationFrame(frame);
  }, [activeStep, measureOverflow]);

  useEffect(() => {
    measureOverflow();
    window.addEventListener('resize', measureOverflow);
    return () => window.removeEventListener('resize', measureOverflow);
  }, [measureOverflow, items.length]);

  return (
    <Box
      component="nav"
      aria-label="Create strategy steps"
      data-testid={STRATEGY_LIST.wizardRail}
      sx={{
        // FP-3: the spine stays under the dialog header — only the step body
        // scrolls (Task B9d must-fix 1). `relative` anchors the edge fades.
        position: 'sticky',
        top: 0,
        zIndex: 3,
        backgroundColor: 'background.default',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      {/* MF-4: the edges SAY that the track continues. Rendered only while it
          actually overflows, so wide layouts are byte-identical. */}
      {(['start', 'end'] as const).map((edge) => (overflow[edge] ? (
        <Box
          key={edge}
          aria-hidden
          data-testid={edge === 'start'
            ? STRATEGY_LIST.wizardRailFadeStart
            : STRATEGY_LIST.wizardRailFadeEnd}
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            width: 28,
            pointerEvents: 'none',
            zIndex: 1,
            ...(edge === 'start' ? { left: 0 } : { right: 0 }),
            background: (theme) => `linear-gradient(to ${edge === 'start' ? 'right' : 'left'}, `
              + `${theme.palette.background.default}, `
              + `${alpha(theme.palette.background.default, 0)})`,
          }}
        />
      ) : null))}
      <Box
        ref={stripRef}
        data-testid={STRATEGY_LIST.wizardRailStrip}
        onScroll={measureOverflow}
        sx={{
          pt: 1.75,
          pb: 1,
          // Must-fix 5 (B9d) / MF-4: the rail is its OWN scroll strip below the
          // track width, with a thin visible scrollbar rather than a silent
          // overflow.
          overflowX: 'auto',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'action.disabled',
            borderRadius: 3,
          },
        }}
      >
        <Stepper
          nonLinear
          alternativeLabel
          activeStep={wizardStepIndex(activeStep)}
          sx={{
          // The track asks for ~108px PER STEP, so an eleventh step scrolls
          // instead of squeezing every label (MF-4).
          minWidth: Math.max(WIZARD_RAIL_MIN_WIDTH, items.length * WIZARD_RAIL_STEP_MIN_WIDTH),
          // Theme tokens only (global constraint): the inactive numeral disc is
          // `text.secondary`, never a hardcoded grey.
          '& .MuiStepIcon-root': { color: 'text.secondary' },
          '& .MuiStepIcon-root.Mui-active': { color: 'primary.main' },
          '& .MuiStepIcon-root.Mui-completed': { color: 'primary.main' },
          '& .MuiStepIcon-root.Mui-error': { color: 'error.main' },
        }}
      >
          {items.map((item) => {
            const isActive = item.key === activeStep;
            return (
            // The ACTIVE step is the blue NUMERAL, never a ✓ (approved canvas
            // v3.3; MUI renders a check for `Mui-active Mui-completed`).
            <Step key={item.key} completed={item.status === 'done' && !isActive}>
              <StepButton
                onClick={() => onStepChange(item.key)}
                aria-current={isActive ? 'step' : undefined}
                data-testid={STRATEGY_LIST.wizardRailStep(item.key)}
                // The caption belongs on the BUTTON: StepButton owns `optional`
                // and drops the prop when it is set on its StepLabel child.
                optional={item.caption
                  ? (
                    <Typography
                      variant="caption"
                      color={item.status === 'error' ? 'error.main' : 'text.secondary'}
                      // The full string stays reachable to a pointer AND to a
                      // screen reader when the 118px caption ellipsises.
                      title={item.caption}
                      sx={{
                        display: 'block',
                        maxWidth: 118,
                        mx: 'auto',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {item.caption}
                    </Typography>
                  )
                  : undefined}
              >
                <StepLabel error={item.status === 'error'}>{item.label}</StepLabel>
              </StepButton>
            </Step>
          );
          })}
        </Stepper>
      </Box>
    </Box>
  );
};

WizardRail.displayName = 'WizardRail';
